import { v4 as uuidv4 } from 'uuid';

/**
 * Outcome of the SSO gate for a location, telling the caller what to do next.
 * - `allow`: render the menu as usual
 * - `login`: no session, kick off the SSO flow through /login
 * - `logout`: authenticated with a session that did not come from this location's identity provider. Clear it and start the SSO flow
 * - `denied`: the session came back without a matching provider even after an SSO login, show the access denied view
 */
type SsoGateResult =
	| { action: 'allow' }
	| { action: 'login' }
	| { action: 'logout' }
	| { action: 'denied' };

const SSO_RETURN_KEY = 'ssoReturn';
const SSO_RECOVERY_KEY = 'ssoRecoveryAttempted';
const SILENT_AUTH_ERRORS: string[] = ['login_required', 'interaction_required', 'consent_required'];

/**
 * The URI the provider redirects back to.
 *
 * @return {string}
 */
export function getSsoRedirectUri(): string {
	return `${window.location.origin}/login/callback`;
}

/**
 * Base64url encode bytes, the encoding PKCE requires for both the verifier and the challenge.
 *
 * @param {Uint8Array} bytes
 * @return {string}
 */
function base64UrlEncode(bytes: Uint8Array): string {
	let binary: string = '';
	bytes.forEach(byte => binary += String.fromCharCode(byte));

	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate the PKCE code verifier. It never leaves the browser except in the final exchange call,
 * so a code lifted out of the callback URL cannot be redeemed on its own.
 * 32 bytes is the size RFC 7636 recommends and encodes to exactly 43 characters.
 *
 * @return {string}
 */
export function generateCodeVerifier(): string {
	const bytes = new Uint8Array(32);
	window.crypto.getRandomValues(bytes);

	return base64UrlEncode(bytes);
}

/**
 * Derive the S256 code challenge from a verifier. crypto.subtle is only available in a secure
 * context, so serving the app over plain HTTP fails here rather than silently dropping PKCE.
 *
 * @param {string} verifier
 * @return {Promise<string>}
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
	if (!window.crypto?.subtle) {
		throw new Error('PKCE requires a secure context, serve the app over HTTPS');
	}

	const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

	return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Build the provider's authorize URL for a location.
 *
 * @param {Sso} sso
 * @param {string} state
 * @param {string} codeChallenge
 * @param {boolean} silent
 * @return {string}
 */
export function buildSsoAuthorizeUrl(sso: Sso, state: string, codeChallenge: string, silent: boolean): string {
	const params = new URLSearchParams({
		client_id: sso.clientId,
		response_type: 'code',
		redirect_uri: getSsoRedirectUri(),
		response_mode: 'query',
		scope: sso.scope,
		state,
		code_challenge: codeChallenge,
		code_challenge_method: 'S256'
	});

	// Silent re-authentication against the provider session, so the user never sees a login screen
	if (silent) {
		params.set('prompt', 'none');
	}

	return `${sso.authorizeUrl}?${params.toString()}`;
}

/**
 * Whether the provider refused a silent authentication and needs the user to interact. Retrying
 * once without prompt=none turns a dead end into a normal login screen.
 *
 * @param {string} error
 * @return {boolean}
 */
export function isSilentAuthError(error: string): boolean {
	return SILENT_AUTH_ERRORS.includes(error);
}

/**
 * Read a single valued query parameter. A repeated parameter arrives as an array and is treated as
 * absent, since every OAuth parameter we read is single valued.
 *
 * @param {string | (string | null)[]} value
 * @return {string}
 */
export function getSsoQueryParam(value: string | (string | null)[]): string {
	return typeof value === 'string' ? value : '';
}

/**
 * Read back the context saved before the redirect out to the SSO provider. Returns null when
 * nothing was stashed or the stored value is unreadable, which means there is no trustworthy place
 * to send the user and no state to validate against.
 *
 * @return {SsoReturnContext | null}
 */
export function readSsoReturnContext(): SsoReturnContext | null {
	const stored = sessionStorage.getItem(SSO_RETURN_KEY);

	if (!stored) {
		return null;
	}

	try {
		const parsed = JSON.parse(stored);

		if (!parsed?.slug || !parsed?.state || !parsed?.codeVerifier) {
			return null;
		}

		return {
			slug: parsed.slug,
			query: parsed.query || {},
			state: parsed.state,
			codeVerifier: parsed.codeVerifier,
			retried: !!parsed.retried
		};
	}
	catch (error) {
		return null;
	}
}

/**
 * Hand the browser off to the provider. Issues the state and PKCE verifier, stashes both with the
 * location to return to, and navigates.
 *
 * @param {Sso} sso
 * @param {string} slug
 * @param {Record<string, string | (string | null)[]>} query
 * @param {boolean} silent
 * @param {boolean} retried
 * @return {Promise<void>}
 */
export async function redirectToSsoProvider(sso: Sso, slug: string, query: Record<string, string | (string | null)[]>, silent: boolean, retried: boolean = false): Promise<void> {
	const state: string = uuidv4();
	const codeVerifier: string = generateCodeVerifier();
	const codeChallenge: string = await generateCodeChallenge(codeVerifier);

	saveSsoReturnContext({ slug, query, state, codeVerifier, retried });

	window.location.href = buildSsoAuthorizeUrl(sso, state, codeChallenge, silent);
}

/**
 * Save where to come back to once the provider redirects the browser back to us, together with the
 * state to validate.
 *
 * @param {SsoReturnContext} context
 * @return {void}
 */
export function saveSsoReturnContext(context: SsoReturnContext): void {
	sessionStorage.setItem(SSO_RETURN_KEY, JSON.stringify(context));
}

export function clearSsoReturnContext(): void {
	sessionStorage.removeItem(SSO_RETURN_KEY);
}

export function hasAttemptedSsoRecovery(): boolean {
	return sessionStorage.getItem(SSO_RECOVERY_KEY) === 'true';
}

export function markSsoRecoveryAttempted(): void {
	sessionStorage.setItem(SSO_RECOVERY_KEY, 'true');
}

export function clearSsoRecoveryAttempt(): void {
	sessionStorage.removeItem(SSO_RECOVERY_KEY);
}

/**
 * Decide what the location should do with the current session.
 *
 * A session only counts as an SSO session when the provider it was authenticated through
 * matches the provider configured for this location. Anything else (a regular account, or a
 * session from another provider) is cleared so the SSO flow can start.
 *
 * @param {boolean} isAuthenticated
 * @param {string | null} sessionProvider - provider returned with the session by the backend
 * @param {string} locationProvider - `sso.type` of the location being opened
 * @param {boolean} recoveryAttempted - whether this tab already cleared a session once; a second
 *   mismatch means the backend is not returning a matching provider and retrying would loop forever
 * @return {SsoGateResult}
 */
export function resolveSsoGate(isAuthenticated: boolean, sessionProvider: string | null, locationProvider: string, recoveryAttempted: boolean): SsoGateResult {
	if (!isAuthenticated) {
		return { action: 'login' };
	}

	const matchesLocation = !!sessionProvider && sessionProvider.toLowerCase() === locationProvider.toLowerCase();

	if (!matchesLocation) {
		return recoveryAttempted ? { action: 'denied' } : { action: 'logout' };
	}

	return { action: 'allow' };
}
